// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useState } from 'react';
import styled from '@emotion/styled';

import { ProviderType } from 'domains/models/account/providerType';
import {
  spacing,
  mediaMax,
  fontSize,
  borderRadius,
  fontWeight,
} from 'presentations/views/styles';
import {
  white,
  gray02,
  gray04,
  secondaryPale,
  secondary,
} from 'presentations/views/styles/color';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import Select from 'presentations/views/components/atoms/Select';
import IconButton from 'presentations/views/components/atoms/IconButton';
import Button from 'presentations/views/components/atoms/Button';
import Image from 'presentations/views/components/atoms/Image';
import Icon from 'presentations/views/components/atoms/Icon';
import Switch from 'presentations/views/components/atoms/Switch';
import Checkbox from 'presentations/views/components/atoms/Checkbox';
import { UserCalendarCreate } from 'presentations/api/schema/user_calendar';
import {
  AccountCalendarListItem,
  AccountListItem,
} from 'presentations/api/schema/account';
import { DisplayType } from 'domains/models/user_calendar/displayType';
import { VisibilityType } from 'domains/models/user_calendar/visibilityType';
import { ErrorList } from 'domains/models';
import {
  apiPath,
  calendarIntegrationsNewPath,
  helpPath,
} from 'presentations/routes/path';
import { destroy } from 'infras/rest_client';
import { useRouter } from 'next/router';

export interface Props {
  accounts: AccountListItem[];
  accountCalendars: AccountCalendarListItem[];
  selectedCalendars: UserCalendarCreate[];
  calendarErrors: ErrorList;
  onCalendarSelect?: any;
  onCalendarWritable?: any;
  onClickAccountDelete?: any;
  isAccountDeletable?: boolean;
  onChangeCalendarShare?: any;
  isLoading?: boolean;
  googleAuthUrl: string;
  microsoftAuthUrl: string;
  hideCalendarSetting?: boolean;
}

export const accountIconName = (providerType: ProviderType) => {
  switch (providerType) {
    case ProviderType.Google:
      return 'Google';
    case ProviderType.Facebook:
      return 'Facebook';
    case ProviderType.Microsoft:
      return 'Microsoft';
    case ProviderType.Apple:
      return 'Apple';
    default:
      break;
  }
  return '';
};

export const LoadingComponent = () => (
  <AccountContainer>
    <Account>
      <AccountSummary>
        <AccountIcon>
          <Skeleton width="32px" height="32px" variant="circular" />
        </AccountIcon>
        <Skeleton width="200px" height="32px" />
      </AccountSummary>
      <AccountAction>
        <Skeleton width={24} height={24} />
      </AccountAction>
    </Account>
    <CalendarList>
      <CalendarListItem>
        <Skeleton width="240px" variant="text" />
      </CalendarListItem>
      <CalendarListItem>
        <Skeleton width="240px" variant="text" />
      </CalendarListItem>
      <CalendarListItem>
        <Skeleton width="240px" variant="text" />
      </CalendarListItem>
    </CalendarList>
  </AccountContainer>
);

export default function Component(props: Props) {
  const router = useRouter();
  const t = translateInComponent(translations);

  const [
    isLoadingReconnectAccountId,
    setIsLoadingReconnectAccountId,
  ] = useState<number | null>(null);

  const accountCalendars = props.accountCalendars || [];
  if (props.isLoading) {
    return (
      <>
        <LoadingComponent />
        <LoadingComponent />
        <LoadingComponent />
      </>
    );
  }

  const shareOptions = [
    { text: t('shareOption_1'), value: 1 },
    { text: t('shareOption_2'), value: 2 },
    { text: t('shareOption_3'), value: 3 },
  ];

  const onClickReconnect = async (account: AccountListItem) => {
    // 再連携前に一度連携情報をリセットする
    // (ただし、リクエストの成功/失敗は無視して、再連携にはすすめる)
    setIsLoadingReconnectAccountId(account.id);
    await destroy(apiPath(`/v1/accounts/${account.id}/delete`));
    setIsLoadingReconnectAccountId(null);

    switch (account.providerType) {
      case ProviderType.Google:
        window.open(props.googleAuthUrl);
        break;
      case ProviderType.Microsoft:
        window.open(props.microsoftAuthUrl);
        break;
      case ProviderType.Apple:
        router.push(calendarIntegrationsNewPath());
        break;
    }
  };

  return (
    <>
      {props.accounts.map((account) => {
        const name = accountIconName(account.providerType);
        const calendarList = accountCalendars.filter(
          (list) => list.id === account.id,
        )[0];
        // 連携エラーが起きているかcheck
        const calendarErrorKey = Object.keys(props.calendarErrors).find(
          (key) => Number(key) === account.id,
        );
        const calendarError = calendarErrorKey
          ? props.calendarErrors[calendarErrorKey].filter(
              (e) =>
                typeof e === 'object' &&
                (e.code === 'WARN-204002' || e.code === 'WARN-204004'),
            )
          : null;
        return (
          <AccountContainer key={account.id}>
            <Account>
              <AccountSummary>
                <AccountIcon>
                  <Image
                    width={32}
                    src={`account/${name.toLocaleLowerCase()}.svg`}
                    alt={name}
                  />
                </AccountIcon>
                <AccountName>{account.email}</AccountName>
              </AccountSummary>
              {props.isAccountDeletable && (
                <AccountAction>
                  <IconButton
                    onClick={(e: any) => props.onClickAccountDelete(account.id)}
                  >
                    <Icon name="delete" />
                  </IconButton>
                </AccountAction>
              )}
            </Account>
            {!calendarError ? (
              calendarList && (
                <>
                  <CalendarListHeader>
                    <CalendarName>{t('Calendar')}</CalendarName>
                    {!props.hideCalendarSetting && (
                      <CalendarSetting>
                        <CalendarSettingShare>
                          {t('Share')}
                        </CalendarSettingShare>
                        <CalendarSettingPost>
                          {t('Add to calendar')}
                        </CalendarSettingPost>
                      </CalendarSetting>
                    )}
                  </CalendarListHeader>
                  <CalendarList>
                    {calendarList.calendars.map((calendar, index) => {
                      const userCalendar = props.selectedCalendars.find(
                        (c) =>
                          c.accountId === account.id &&
                          c.calendarId === calendar.id,
                      );
                      let shareOption = 2;
                      if (userCalendar) {
                        if (userCalendar.displayType === DisplayType.Private) {
                          shareOption = 1;
                        } else if (
                          userCalendar.displayType === DisplayType.TeamPublic &&
                          userCalendar.visibilityType === VisibilityType.Busy
                        ) {
                          shareOption = 2;
                        } else {
                          shareOption = 3;
                        }
                      }

                      return (
                        <CalendarListItem key={index}>
                          <CalendarName>
                            <Checkbox
                              value={index}
                              onChange={(e: SyntheticEvent) =>
                                props.onCalendarSelect(account.id, calendar)
                              }
                              isChecked={!!userCalendar}
                              label={calendar.name}
                            />
                          </CalendarName>
                          {!props.hideCalendarSetting && userCalendar && (
                            <CalendarSetting>
                              <CalendarSettingShare>
                                <Select
                                  size="small"
                                  width={180}
                                  value={Number(shareOption)}
                                  onChange={(e: SyntheticEvent<any>) =>
                                    props.onChangeCalendarShare(
                                      account.id,
                                      calendar.id,
                                      e.currentTarget.value,
                                    )
                                  }
                                  options={shareOptions}
                                />
                              </CalendarSettingShare>
                              <CalendarSettingPost>
                                <Switch
                                  onChange={(e: SyntheticEvent) =>
                                    props.onCalendarWritable(
                                      account.id,
                                      calendar.id,
                                    )
                                  }
                                  isChecked={userCalendar.isWritable}
                                />
                              </CalendarSettingPost>
                            </CalendarSetting>
                          )}
                        </CalendarListItem>
                      );
                    })}
                  </CalendarList>
                </>
              )
            ) : (
              <ErrorContainer>
                <ErrorMessageBox>
                  <ErrorMessageContainer>
                    <ErrorTitle>
                      <ErrorIcon name="alert-circle-outline" />
                      {(calendarError[0] || {}).message ||
                        t('Calendar Error Title')}
                    </ErrorTitle>
                    <ErrorMessage>
                      {(calendarError[0] || {}).code === 'WARN-204004' ? (
                        <>
                          {t('Calendar Error Message for scope.0', {
                            email: account.email,
                          })}
                          <a
                            href={helpPath('7155257#h_0e795a06d5')}
                            target="_blank"
                          >
                            {t('Calendar Error Message for scope.1')}
                          </a>
                          {t('Calendar Error Message for scope.2')}
                        </>
                      ) : (
                        t('Calendar Error Message')
                      )}
                    </ErrorMessage>
                  </ErrorMessageContainer>
                  <ReconnectButton
                    color="primary"
                    onClick={() => onClickReconnect(account)}
                    isLoading={isLoadingReconnectAccountId === account.id}
                  >
                    {t('Reconnect')}
                  </ReconnectButton>
                </ErrorMessageBox>
              </ErrorContainer>
            )}
          </AccountContainer>
        );
      })}
    </>
  );
}

const AccountContainer = styled.div`
  padding: ${spacing(48)};
  margin-bottom: ${spacing(48)};
  :last-of-type {
    margin-bottom: 0;
  }
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(16)};
  background-color: ${white};
  ${mediaMax.tablet} {
    padding: ${spacing(24)};
    margin-bottom: ${spacing(24)};
  }
  ${mediaMax.smartphone} {
    padding: ${spacing(16)};
    margin-bottom: ${spacing(16)};
  }
`;
const Account = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: ${spacing(8)};
`;
const CalendarList = styled.div`
  margin-top: ${spacing(16)};
`;
const CalendarListHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${spacing(24)};
  margin-top: ${spacing(24)};
  margin-left: ${spacing(8)};
  border-top: 1px solid ${gray04};
  color: ${gray02};
  font-size: ${fontSize(12)};
`;
const CalendarListItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 16px;
`;
const CalendarName = styled.div`
  word-break: break-all;
`;
const CalendarSetting = styled.div`
  display: flex;
`;
const CalendarSettingPost = styled.div`
  width: 80px;
  text-align: right;
`;
const CalendarSettingShare = styled.div`
  width: 160px;
  text-align: center;
  ${mediaMax.smartphone} {
    display: none;
  }
`;
const AccountSummary = styled.div`
  display: flex;
  align-items: center;
`;
const AccountAction = styled.div``;
const AccountIcon = styled.div`
  margin-right: ${spacing(16)};
  ${mediaMax.tablet} {
    margin-right: ${spacing(12)};
  }
`;
const AccountName = styled.div`
  ${mediaMax.tablet} {
    max-width: 48vw;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  ${mediaMax.smartphone} {
    max-width: 140px;
  }
  ${mediaMax.xs} {
    max-width: 120px;
  }
`;

const ErrorContainer = styled.div`
  padding-top: ${spacing(24)};
  margin-top: ${spacing(24)};
  margin-left: ${spacing(8)};
  border-top: 1px solid ${gray04};
`;
const ErrorMessageBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing(24)};
  background-color: ${secondaryPale};
  border-radius: ${borderRadius(16)};

  ${mediaMax.desktop} {
    flex-direction: column;
  }
  ${mediaMax.tablet} {
    padding: ${spacing(12)};
  }
`;
const ErrorMessageContainer = styled.div`
  margin-right: ${spacing(12)};

  ${mediaMax.desktop} {
    margin-right: 0;
  }
`;
const ErrorTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(12)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    font-size: ${fontSize(11)};
  }
`;
const ErrorIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  width: 20px !important;
  height: 20px !important;
  color: ${secondary};

  ${mediaMax.tablet} {
    width: 16px !important;
    height: 16px !important;
  }
`;
const ErrorMessage = styled.div`
  ${mediaMax.desktop} {
    margin-bottom: ${spacing(12)};
  }
  ${mediaMax.tablet} {
    font-size: ${fontSize(10)};
  }
`;
const ReconnectButton = styled(Button)`
  width: 160px;
  height: 46px;
  flex-shrink: 0;

  ${mediaMax.tablet} {
    width: 120px;
    height: 31px;
    font-size: ${fontSize(11)};
  }
`;
